import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle, XCircle, Loader2, CreditCard } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [quoteData, setQuoteData] = useState(null);
  
  // Paramètres de l'URL
  const quoteId = searchParams.get('quote');
  const amount = parseFloat(searchParams.get('amount'));
  const currency = searchParams.get('currency') || 'EUR';

  useEffect(() => {
    if (!quoteId || !amount) {
      setError("Paramètres de paiement manquants");
      setLoading(false);
      return;
    }

    // Charger les informations du devis
    setQuoteData({
      id: quoteId,
      amount: amount,
      currency: currency
    });
    setLoading(false);
  }, [quoteId, amount, currency]);

  const createOrder = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/payments/create-order`, {
        quote_id: quoteId,
        amount: amount,
        currency: currency,
        description: `Paiement devis Z&N Solar - ${quoteId}`
      });
      
      return response.data.order_id;
    } catch (err) {
      console.error('Error creating order:', err);
      setError("Erreur lors de la création de la commande PayPal");
      throw err;
    }
  };

  const onApprove = async (data) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/payments/capture-order`, {
        order_id: data.orderID
      });
      
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/payment/success');
        }, 3000);
      }
    } catch (err) {
      console.error('Error capturing order:', err);
      setError("Erreur lors de la capture du paiement");
    }
  };

  const onError = (err) => {
    console.error('PayPal error:', err);
    setError("Une erreur est survenue avec PayPal. Veuillez réessayer.");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-yellow-600 mb-4" />
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!quoteId || !amount) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-red-600 flex items-center">
              <XCircle className="w-6 h-6 mr-2" />
              Lien invalide
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">Ce lien de paiement est invalide ou a expiré.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-green-600 flex items-center">
              <CheckCircle className="w-6 h-6 mr-2" />
              Paiement réussi !
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Votre paiement a été traité avec succès. Vous allez être redirigé...
            </p>
            <Badge className="bg-green-100 text-green-800">
              Montant payé : {amount} {currency}
            </Badge>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Paiement Sécurisé
          </h1>
          <p className="text-gray-600">Z&N Solar - L'excellence sans compromis</p>
        </div>

        {/* Informations de paiement */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CreditCard className="w-5 h-5 mr-2 text-yellow-600" />
              Détails du paiement
            </CardTitle>
            <CardDescription>
              Numéro de devis : {quoteId}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="flex justify-between items-center text-lg">
                <span className="font-semibold">Montant à payer :</span>
                <span className="text-2xl font-bold text-yellow-600">
                  {amount.toFixed(2)} {currency}
                </span>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Le paiement est sécurisé par PayPal. Vous serez redirigé vers PayPal pour finaliser la transaction.
            </p>
          </CardContent>
        </Card>

        {/* Erreur */}
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <XCircle className="w-4 h-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {/* Boutons PayPal */}
        <Card>
          <CardHeader>
            <CardTitle>Payer avec PayPal</CardTitle>
            <CardDescription>
              Cliquez sur le bouton ci-dessous pour procéder au paiement
            </CardDescription>
          </CardHeader>
          <CardContent>
            {PAYPAL_CLIENT_ID ? (
              <PayPalScriptProvider 
                options={{ 
                  "client-id": PAYPAL_CLIENT_ID,
                  currency: currency,
                  intent: "capture"
                }}
              >
                <PayPalButtons
                  style={{
                    layout: "vertical",
                    color: "gold",
                    shape: "rect",
                    label: "pay"
                  }}
                  createOrder={createOrder}
                  onApprove={onApprove}
                  onError={onError}
                />
              </PayPalScriptProvider>
            ) : (
              <Alert className="border-yellow-200 bg-yellow-50">
                <AlertDescription className="text-yellow-800">
                  Configuration PayPal manquante. Contactez l'administrateur.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Informations légales */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>En cliquant sur "Payer avec PayPal", vous acceptez nos conditions générales de vente.</p>
          <p className="mt-2">Transaction sécurisée par PayPal</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
